import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionTab } from "primereact/accordion";
import { getFormalLocale } from "../utils/appUtils";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  getChangeComparedToPrevYear,
  getChangePercentageComparedToPrevYear,
  getDivideAmount,
  getMultiplyAmount,
} from "../utils/summaryUtils";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";
import { ReportType } from "../api/models/report.models";

export type AccordionRow = {
  key: string;
  type: "business" | "consumer";
  label: string;
  combinedWeight: number;
  recycledWeight: number;
  ecomodMaterials: {
    key: string;
    label: string;
    combinedWeight: number;
    recycledWeight: number;
    price?: number;
  }[];
  price?: number;
};

const SummaryAccordionContainer = styled.div`
  margin-bottom: 3rem;

  & h3 {
    font-size: 18px;
    font-weight: 600;
    margin-left: 10px;
    margin-bottom: 2rem;
  }
`;

const AccordionListHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  font-size: 14px;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  font-weight: 500;
  margin: 0 24px 0 1rem;

  @media (max-width: 1500px) {
    font-size: 12px;
  }
`;

const AccordionListItem = styled.div`
  flex-grow: 1;
  color: #333;
  margin-right: 15px;

  &.name-container {
    min-width: 500px;

    @media (max-width: 1500px) {
      min-width: 300px;
    }
  }
`;

const AccordionContentList = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  font-size: 16px;
  font-weight: 400;
  width: 100%;

  @media (max-width: 1500px) {
    font-size: 14px;
  }
`;

const AccordionInnerContent = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  font-size: 16px;
  font-weight: 400;
  margin: 0 3px 0 0;

  &:not(:first-child) {
    margin-top: 10px;
  }

  @media (max-width: 1500px) {
    font-size: 14px;
  }
`;

const StyledAccordion = styled(Accordion)`
  & .p-accordion-header-text {
    font-size: 16px;
    font-weight: 500;
  }

  .p-accordion-tab-active .p-accordion-header {
    border-bottom: none;
    background-color: #e6e6e6;
    border-radius: 6px 6px 0 0;

    & .p-accordion-header-link svg {
      transform: rotate(180deg);
    }

    &:not(.p-disabled).p-highlight .p-accordion-header-link {
      background-color: #e6e6e6;
    }
  }

  .p-accordion-tab-active .p-accordion-content {
    background-color: #e6e6e6;
    border: none;
    border-radius: 0 0 6px 6px;
  }

  .p-accordion-header {
    background-color: #fdfdfd;
    font-size: 16px;
    margin-top: 1.5rem;
    height: fit-content;
    border-radius: 6px;
    border: 2px solid #e6e6e6;

    &:not(.p-disabled).p-highlight .p-accordion-header-link {
      background-color: #fdfdfd;
      border: none;
    }

    & .p-accordion-header-link {
      background-color: #fdfdfd;
      border: none;

      & svg {
        margin-right: 1rem;
        width: 12px;
        height: 12px;
        position: absolute;
        right: 5px;
        transform: rotate(90deg);
      }
    }
  }

  .p-accordion-content {
    border: none;
    padding: 1.5rem 1rem;
    border-radius: 6px;
    margin-top: -6px;
  }
`;

interface Props {
  tableData: AccordionRow[];
  header: string;
  prevYearData?: AccordionRow[];
  prevYearReportingCycle: ReportingCycleEnum;
  currentYearReportingCycle: ReportingCycleEnum;
  reportType?: ReportType;
}

const SummaryAccordion: React.FC<Props> = ({
  tableData,
  header,
  prevYearData,
  prevYearReportingCycle,
  currentYearReportingCycle,
  reportType,
}: Props) => {
  const { t } = useTranslation();
  const [locale] = useUIStore(useShallow((state: UIState) => [state.locale]));
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  const formalLocale = getFormalLocale(locale);

  const onTabChange = (e: any) => {
    setActiveTabIndex(e.index);
  };

  const numberToFixed = (number: number) => {
    const fixedNumber = number.toFixed(2);
    return fixedNumber.endsWith(".00") ? fixedNumber.slice(0, -3) : fixedNumber;
  };

  const toMeaningfulNumber = (value: number) => {
    const roundedValue = Math.round(value * 100) / 100;
    return roundedValue.toLocaleString(formalLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  };

  const dividePreviousYearBy = getDivideAmount(
    prevYearReportingCycle,
    currentYearReportingCycle,
  );

  const multiplyPreviousYearBy = getMultiplyAmount(
    prevYearReportingCycle,
    currentYearReportingCycle,
  );

  const headerTemplate = (row: AccordionRow) => {
    const totalWeightFormatted = toMeaningfulNumber(row.combinedWeight);
    const recycledWeightFormatted = toMeaningfulNumber(row.recycledWeight);
    const prevYearWeight = prevYearData?.find(
      (item) => item.key === row.key,
    )?.combinedWeight;

    return (
      <AccordionContentList>
        <AccordionListItem
          style={{ paddingRight: "1rem" }}
          className="name-container"
        >
          {row?.label}
        </AccordionListItem>
        <AccordionListItem>{`${totalWeightFormatted} kg`}</AccordionListItem>
        <AccordionListItem>{`${recycledWeightFormatted} kg`}</AccordionListItem>
        <AccordionListItem>
          {prevYearWeight
            ? getChangeComparedToPrevYear(
                row.combinedWeight,
                prevYearWeight,
                dividePreviousYearBy,
                multiplyPreviousYearBy,
              ).weightChange.toFixed(2) + " kg"
            : "-"}
        </AccordionListItem>
        <AccordionListItem>
          {prevYearWeight
            ? getChangePercentageComparedToPrevYear(
                row.combinedWeight,
                prevYearWeight,
                dividePreviousYearBy,
                multiplyPreviousYearBy,
              )
            : "-"}
        </AccordionListItem>
        <AccordionListItem>
          {row.price ? `${numberToFixed(row.price)} €` : "-"}
        </AccordionListItem>
      </AccordionContentList>
    );
  };

  return (
    <SummaryAccordionContainer className="summary-accordion">
      <h3>{header}</h3>
      <AccordionListHeader style={{ marginBottom: "-1rem" }}>
        <AccordionListItem
          style={{ paddingRight: "1rem" }}
          className="name-container"
        >
          {t("header-material")}
        </AccordionListItem>
        <AccordionListItem>{t("header-total-amount")}</AccordionListItem>
        <AccordionListItem>{t("header-recycled-weight")}</AccordionListItem>
        <AccordionListItem>{t("header-change-kg")}</AccordionListItem>
        <AccordionListItem>{t("header-change-percentage")}</AccordionListItem>
        <AccordionListItem>
          {t("header-price") + ` ${t("label-vat-amount")}`}
        </AccordionListItem>
      </AccordionListHeader>
      <StyledAccordion activeIndex={activeTabIndex} onTabChange={onTabChange}>
        {tableData?.map((row, index) => {
          if (!row.combinedWeight) return null;

          return (
            <AccordionTab
              key={index}
              className="accordion-tab"
              headerTemplate={() => headerTemplate(row)}
            >
              {row.ecomodMaterials.map((ecomod, idx) => {
                if (!ecomod.combinedWeight) return null;

                const prevYearWeight = prevYearData
                  ?.find((item) => item.key === row.key)
                  ?.ecomodMaterials.find(
                    (mat) => mat.key === ecomod.key,
                  )?.combinedWeight;

                const ecomodFormattedWeight = toMeaningfulNumber(
                  ecomod.combinedWeight,
                );

                const ecomodRecycledWeight = toMeaningfulNumber(
                  ecomod.recycledWeight,
                );

                return (
                  <AccordionInnerContent key={idx}>
                    <AccordionListItem
                      style={{
                        paddingLeft: "20px",
                        paddingRight: "1rem",
                      }}
                      className="name-container"
                    >
                      {ecomod.label}
                    </AccordionListItem>
                    <AccordionListItem>
                      {`${ecomodFormattedWeight} kg`}
                    </AccordionListItem>
                    <AccordionListItem>
                      {`${ecomodRecycledWeight} kg`}
                    </AccordionListItem>
                    <AccordionListItem>
                      {prevYearWeight
                        ? getChangeComparedToPrevYear(
                            ecomod.combinedWeight,
                            prevYearWeight,
                            dividePreviousYearBy,
                            multiplyPreviousYearBy,
                          ).weightChange.toFixed(2) + " kg"
                        : "-"}
                    </AccordionListItem>
                    <AccordionListItem>
                      {prevYearWeight
                        ? getChangePercentageComparedToPrevYear(
                            ecomod.combinedWeight,
                            prevYearWeight,
                            dividePreviousYearBy,
                            multiplyPreviousYearBy,
                          )
                        : "-"}
                    </AccordionListItem>
                    <AccordionListItem>
                      {ecomod.price ? `${numberToFixed(ecomod.price)} €` : "-"}
                    </AccordionListItem>
                  </AccordionInnerContent>
                );
              })}
            </AccordionTab>
          );
        })}
      </StyledAccordion>
    </SummaryAccordionContainer>
  );
};

export default SummaryAccordion;
